import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../styles/components/VisitorCounter.css';
import { getWebsiteVisitorCounts, trackWebsiteVisit } from '../services/api';

const VISITOR_ID_KEY = 'costech_website_visitor_id';
const VISITOR_COUNTS_CACHE_KEY = 'costech_website_visitor_counts';

const createVisitorId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getVisitorId = () => {
  const existingVisitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (existingVisitorId) {
    return existingVisitorId;
  }

  const visitorId = createVisitorId();
  localStorage.setItem(VISITOR_ID_KEY, visitorId);
  return visitorId;
};

const getCachedCounts = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(VISITOR_COUNTS_CACHE_KEY) || '{}');
    return {
      month: Number.isFinite(Number(cached.month)) ? Number(cached.month) : null,
      today: Number.isFinite(Number(cached.today)) ? Number(cached.today) : null,
    };
  } catch (error) {
    return {
      month: null,
      today: null,
    };
  }
};

const VisitorCounter = () => {
  const [counts, setCounts] = useState({
    month: null,
    today: null,
  });
  const [displayCounts, setDisplayCounts] = useState({
    month: 0,
    today: 0,
  });
  const [loading, setLoading] = useState(true);
  const [counterUnavailable, setCounterUnavailable] = useState(false);
  const counterRef = useRef(null);
  const isInView = useInView(counterRef, { once: true, margin: '-50px' });

  useEffect(() => {
    const fetchAndTrackVisit = async () => {
      try {
        const visitorId = getVisitorId();
        const nextCounts = await trackWebsiteVisit(visitorId);
        localStorage.setItem(VISITOR_COUNTS_CACHE_KEY, JSON.stringify(nextCounts));
        setCounterUnavailable(false);
        setCounts(nextCounts);
      } catch (error) {
        console.warn('Visitor counter unavailable:', error.message);
        setCounterUnavailable(true);
        const cachedCounts = getCachedCounts();
        setCounts(cachedCounts);

        try {
          const latestCounts = await getWebsiteVisitorCounts();
          localStorage.setItem(VISITOR_COUNTS_CACHE_KEY, JSON.stringify(latestCounts));
          setCounts(latestCounts);
        } catch (countsError) {
          console.warn('Visitor counter counts unavailable:', countsError.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAndTrackVisit();
  }, []);

  // Animate the count number when in view
  useEffect(() => {
    if (counts.month === null || counts.today === null || !isInView) return;

    const duration = 1500; // ms
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCounts({
        month: Math.floor(counts.month * eased),
        today: Math.floor(counts.today * eased),
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayCounts(counts);
      }
    };

    requestAnimationFrame(animate);
  }, [counts, isInView]);

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const renderDigits = (value) => {
    return formatNumber(value).split('').map((digit, index) => (
      <motion.span
        key={`${index}-${digit}`}
        className={`visitor-digit ${digit === ',' ? 'visitor-separator' : ''}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03, duration: 0.3 }}
      >
        {digit}
      </motion.span>
    ));
  };

  return (
    <motion.div
      ref={counterRef}
      className="visitor-counter"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="visitor-counter-inner">
        <div className="visitor-counter-icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        <div className="visitor-counter-content">
          <span className="visitor-counter-label">Visitors</span>
          <div className="visitor-counter-stats" aria-live="polite">
            {loading ? (
              <span className="visitor-counter-loading">
                <span className="visitor-dot"></span>
                <span className="visitor-dot"></span>
                <span className="visitor-dot"></span>
              </span>
            ) : counterUnavailable && counts.month === null && counts.today === null ? (
              <span className="visitor-counter-unavailable">Temporarily unavailable</span>
            ) : (
              <>
                <div className="visitor-counter-stat">
                  <span className="visitor-counter-period">This Month</span>
                  <div className="visitor-counter-digits">
                    {renderDigits(displayCounts.month)}
                  </div>
                </div>
                <span className="visitor-counter-divider" aria-hidden="true"></span>
                <div className="visitor-counter-stat">
                  <span className="visitor-counter-period">Today</span>
                  <div className="visitor-counter-digits">
                    {renderDigits(displayCounts.today)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VisitorCounter;
